import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Spinner, Button, Collapse, Modal } from 'react-bootstrap';
import PageHeader from '../../components/ui/PageHeader';
import { listarRango, cambiarAtencionTurno } from '../../services/turneroService';
import { colorTramite, bgTramite, formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { AREAS_ATENCION, buscarArea } from '../../constants/areasAtencion';
import { estiloEstado } from '../../constants/estadosTurno';
import { esSinPresentacion, metaTipoTurnoAnses } from '../../utils/tiposTurnoAnses';
import SelectorAreaAtencion from '../../components/recepcion/SelectorAreaAtencion';
import BadgeReingreso from '../../components/recepcion/BadgeReingreso';
import ModalDetalleTurnoAnses from '../../components/ModalDetalleTurnoAnses';
import ModalReingresoCliente, { TIPO_APERTURA_PLAN } from '../../components/recepcion/ModalReingresoCliente';
import { useAuth } from '../../auth/AuthContext';
import turneroStyles from '../../style/pages/Turnero.module.css';
import '../../style/pages/HomePage.css';
import '../../style/pages/AtencionPage.css';

import { apellidoNombre } from '../../utils/nombrePersona';
import { coincideBusqueda } from '../../utils/textoComparable';
const FECHA_DESDE = '2000-01-01';
const FECHA_HASTA = '2100-12-31';
const INTERVALO_ACTUALIZACION_MS = 20000;

function formatearFecha(fechaStr) {
    if (!fechaStr) return '-';
    const partes = String(fechaStr).split('T')[0].split('-');
    if (partes.length !== 3) return fechaStr;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function normalizarHorario(horario) {
    if (!horario) return '00:00';
    return String(horario).substring(0, 5);
}

function formatearHorario(horario) {
    return horario ? normalizarHorario(horario) : '-';
}

// Orden de llegada real (check-in a ASISTIO), no el horario agendado — ver
// TurnoRecepcionEntity.horaLlegada. Turnos viejos sin esa marca (antes de este campo)
// caen al horario agendado como respaldo, para no perder el orden por completo.
function crearMarcaTiempo(turno) {
    if (turno?.horaLlegada) {
        const marcaLlegada = new Date(turno.horaLlegada);
        if (!Number.isNaN(marcaLlegada.getTime())) return marcaLlegada;
    }
    const fecha = turno?.fechaTurno ? `${turno.fechaTurno}T${normalizarHorario(turno.horario)}:00` : '';
    const marca = new Date(fecha);
    return Number.isNaN(marca.getTime()) ? new Date(0) : marca;
}

function campoValor(valor) {
    return valor ? String(valor) : '-';
}

function nombreOperador(turno) {
    return turno?.operadorNombre ? String(turno.operadorNombre) : 'Sin operador';
}

function resolverIdTurno(turno) {
    return turno?.idTurno ?? turno?.id ?? turno?.id_turno;
}

// ============================================
// CHIPS DE FILTRO DE LA COLA — por área de atención (turno.atencion), no por trámite.
// 'todos' y 'sin_asignar' son pseudo-áreas que no viven en AREAS_ATENCION.
// ============================================
function estiloChip(id) {
    if (id === 'todos') return { color: '#64748b', bgColor: '#f1f5f9' };
    if (id === 'sin_asignar') return { color: '#94a3b8', bgColor: '#f1f5f9' };
    const area = buscarArea(id);
    return area ? { color: area.color, bgColor: area.bgColor } : { color: '#64748b', bgColor: '#f1f5f9' };
}

// Clase que tiñe la tarjeta según el estado (fondo, sombra, riel) — es puramente visual,
// las etiquetas/íconos/colores de badge salen de la paleta compartida en getEstadoTurno.
const CLASE_ESTADO_CARD = {
    ASISTIO: 'estado-asistio',
    EN_CURSO: 'estado-en-curso',
    EN_ANSES: 'estado-en-anses',
    FINALIZADO: 'estado-finalizado',
};

const AtencionPage = () => {
    const navigate = useNavigate();
    const { puede } = useAuth();
    const montadoRef = useRef(false);
    const [turnos, setTurnos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [areaSeleccionada, setAreaSeleccionada] = useState('todos');
    const [filtroTexto, setFiltroTexto] = useState('');
    const [detalleAnsesId, setDetalleAnsesId] = useState(null);
    const [mostrarFinalizados, setMostrarFinalizados] = useState(true);

    // Extraído a useCallback (antes vivía inline en el useEffect) para poder recargar la
    // lista después de derivar un turno a otra área, sin tener que remontar la página.
    const cargarTurnos = useCallback(async (silencioso = false) => {
        if (!silencioso) setCargando(true);
        if (!silencioso) setError('');
        try {
            const datos = await listarRango(FECHA_DESDE, FECHA_HASTA);
            if (!montadoRef.current) return;

            const hoy = new Date();
            const fechaHoy = hoy.toISOString().split('T')[0];
            const fechaHoyLocal = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;

            const turnosAtencion = (Array.isArray(datos) ? datos : [])
                .filter((turno) => {
                    const estado = String(turno?.estado || '').trim().toUpperCase();
                    const esAsistio = estado === 'ASISTIO';
                    const esEnCurso = estado === 'EN_CURSO';
                    // El cliente ya salió con el abogado hacia ANSES: sigue siendo un turno
                    // activo del día, solo que no está en la sala esperando ser atendido.
                    const esEnAnses = estado === 'EN_ANSES';
                    const fechaTurno = String(turno?.fechaTurno || '').split('T')[0];
                    const esFinalizado = estado === 'FINALIZADO' && (fechaTurno === fechaHoy || fechaTurno === fechaHoyLocal || !fechaTurno);
                    return esAsistio || esEnCurso || esEnAnses || esFinalizado;
                })
                .sort((a, b) => {
                    const ordenEstado = { 'ASISTIO': 0, 'EN_CURSO': 1, 'EN_ANSES': 2, 'FINALIZADO': 3 };
                    const estadoA = String(a?.estado || '').trim().toUpperCase();
                    const estadoB = String(b?.estado || '').trim().toUpperCase();
                    const ordenA = ordenEstado[estadoA] ?? 4;
                    const ordenB = ordenEstado[estadoB] ?? 4;

                    if (ordenA !== ordenB) return ordenA - ordenB;

                    // En la sala de espera, el reingreso marcado con prioridad pasa primero; entre
                    // prioritarios sigue valiendo el orden de llegada.
                    if (estadoA === 'ASISTIO') {
                        const prioridadA = a?.prioridadReingreso ? 0 : 1;
                        const prioridadB = b?.prioridadReingreso ? 0 : 1;
                        if (prioridadA !== prioridadB) return prioridadA - prioridadB;
                    }

                    const marcaA = crearMarcaTiempo(a).getTime();
                    const marcaB = crearMarcaTiempo(b).getTime();
                    if (marcaA !== marcaB) return marcaA - marcaB;
                    return Number(a?.idTurno || 0) - Number(b?.idTurno || 0);
                });

            setTurnos(turnosAtencion);
        } catch (e) {
            if (!montadoRef.current) return;
            setError(e?.response?.data?.mensaje || 'No se pudieron cargar los turnos de atención.');
        } finally {
            if (montadoRef.current) setCargando(false);
        }
    }, []);

    useEffect(() => {
        montadoRef.current = true;
        cargarTurnos();
        const intervalo = setInterval(() => cargarTurnos(true), INTERVALO_ACTUALIZACION_MS);
        return () => {
            montadoRef.current = false;
            clearInterval(intervalo);
        };
    }, [cargarTurnos]);

    // Separar turnos por estado, y sacar de la cola de atención los acompañamientos a ANSES:
    // ocupan lugar en el turnero y hay que verlos, pero no se atienden como una consulta
    // normal (el cliente espera y un abogado lo lleva al organismo).
    // El que ya salió hacia ANSES (EN_ANSES: solo el plan de pago, que vuelve después como
    // reingreso) no está en el estudio: va con los terminados y no ocupa lugar en la cola.
    const { turnosActivos, turnosAnses, turnosFinalizados } = useMemo(() => {
        const esAnses = (t) => String(t?.modalidad || '').trim().toUpperCase() === 'ANSES';
        const finalizado = (t) => ['FINALIZADO', 'EN_ANSES'].includes(String(t?.estado || '').trim().toUpperCase());

        return {
            turnosActivos: turnos.filter(t => !finalizado(t) && !esAnses(t)),
            turnosAnses: turnos.filter(t => !finalizado(t) && esAnses(t)),
            turnosFinalizados: turnos.filter(finalizado)
        };
    }, [turnos]);

    // Cantidad de turnos activos por área, para el contador de cada chip.
    const contadoresPorArea = useMemo(() => {
        const conteo = {};
        turnosActivos.forEach((t) => {
            const clave = t?.atencion ? String(t.atencion).trim().toUpperCase() : 'sin_asignar';
            conteo[clave] = (conteo[clave] || 0) + 1;
        });
        // Los turnos ANSES se sacan de turnosActivos (carril aparte), pero el chip ANSES tiene que
        // contar los que están en espera en esa sección.
        conteo.ANSES = (conteo.ANSES || 0) + turnosAnses.length;
        return conteo;
    }, [turnosActivos, turnosAnses]);

    // Filtrar turnos activos por área de atención (turno.atencion), no por tipo de trámite,
    // y además por el texto libre de búsqueda (nombre, apellido, DNI o CUIL).
    const turnosActivosFiltrados = useMemo(() => {
        let base = turnosActivos;
        if (areaSeleccionada === 'sin_asignar') {
            base = base.filter((turno) => !turno?.atencion);
        } else if (areaSeleccionada !== 'todos') {
            base = base.filter((turno) => String(turno?.atencion || '').trim().toUpperCase() === areaSeleccionada);
        }
        if (filtroTexto.trim()) {
            // Sin tildes y por palabras: ver coincideBusqueda.
            base = base.filter((turno) => coincideBusqueda(
                [turno?.apellido, turno?.nombre, turno?.dni, turno?.cuil], filtroTexto));
        }
        return base;
    }, [turnosActivos, areaSeleccionada, filtroTexto]);

    const totalTurnosActivos = useMemo(() => turnosActivosFiltrados.length, [turnosActivosFiltrados]);
    const totalFinalizados = useMemo(() => turnosFinalizados.length, [turnosFinalizados]);

    // Función para obtener la clase del área con color
    const getAreaChipClass = (areaId) => {
        const isActive = areaId === areaSeleccionada;
        return `area-chip ${isActive ? 'area-chip-active' : 'area-chip-inactive'}`;
    };

    // Función para obtener el estilo del chip
    const getAreaChipStyle = (areaId) => {
        const { color } = estiloChip(areaId);
        const isActive = areaId === areaSeleccionada;

        if (isActive) {
            return {
                backgroundColor: color,
                color: '#ffffff',
                borderColor: color,
                boxShadow: `0 4px 12px ${color}40`
            };
        }
        return {
            color,
            borderColor: `${color}40`,
            backgroundColor: 'transparent'
        };
    };

    // Función para obtener el estado del turno
    const getEstadoTurno = (turno) => {
        const clave = String(turno?.estado || '').trim().toUpperCase();
        const e = estiloEstado(clave);
        return {
            // ASISTIO en esta pantalla significa "el cliente está esperando"; se muestra con
            // esa etiqueta en vez de "Asistió" (que sí corresponde en el Turnero).
            label: clave === 'ASISTIO' && e.etiquetaEspera ? e.etiquetaEspera : e.etiqueta,
            icon: e.icono,
            bg: e.bg,
            border: e.border,
            text: e.text,
            riel: e.riel,
            className: CLASE_ESTADO_CARD[clave] || 'estado-asistio',
        };
    };

    // Función para navegar a la vista detallada
    const navegarADetalle = (idTurno, turno) => {
        if (!idTurno) return;
        navigate(`/recepcion/atencion/${idTurno}`, { state: { turno } });
    };

    // Pantalla informativa (TV de la sala de espera): su página de configuración define el video y
    // el aviso, y abre el TV con el link que lleva la clave. La clave queda en manos de quien
    // configura el turnero, igual que en el backend (TURNERO_CONFIG).
    const puedeConfigurarPantalla = puede('turnero.config');

    // Derivar un turno a otra área (recepción se equivocó de destino). Se abre desde el
    // kebab de la tarjeta y persiste con PATCH /{idTurno}/atencion.
    const [turnoDerivar, setTurnoDerivar] = useState(null);
    // "Volvió de ANSES" de un turno que salió por el plan de pago (está con los terminados).
    const [turnoVuelveDeAnses, setTurnoVuelveDeAnses] = useState(null);
    const [areaDerivar, setAreaDerivar] = useState('');
    const [guardandoDerivar, setGuardandoDerivar] = useState(false);
    const [errorDerivar, setErrorDerivar] = useState('');

    const abrirDerivar = (turno) => {
        setErrorDerivar('');
        setAreaDerivar(turno?.atencion || '');
        setTurnoDerivar(turno);
    };

    const cerrarDerivar = () => {
        if (guardandoDerivar) return;
        setTurnoDerivar(null);
        setAreaDerivar('');
        setErrorDerivar('');
    };

    const confirmarDerivar = async () => {
        const idTurno = resolverIdTurno(turnoDerivar);
        if (!idTurno || !areaDerivar || guardandoDerivar) return;
        setGuardandoDerivar(true);
        setErrorDerivar('');
        try {
            await cambiarAtencionTurno(idTurno, areaDerivar);
            await cargarTurnos();
            if (montadoRef.current) {
                setTurnoDerivar(null);
                setAreaDerivar('');
            }
        } catch (e) {
            if (montadoRef.current) {
                setErrorDerivar(e?.response?.data?.mensaje || 'No se pudo derivar el turno a la nueva área.');
            }
        } finally {
            if (montadoRef.current) setGuardandoDerivar(false);
        }
    };

    return (
        <div className="homepage-container area-recepcion">
            <PageHeader
                variante="plano"
                icono="person-check-fill"
                titulo="Atención"
                descripcion="Listado de turnos en atención: Asistió, En curso y Finalizado del día actual, ordenados por llegada."
            />

            {/* Botón de Pantalla Informativa - FUERA del header */}
            <div className="atencion-pantalla-container">
                {puedeConfigurarPantalla && (
                    <Button
                        className="rec-btn-accent atencion-btn-pantalla"
                        onClick={() => navigate('/recepcion/pantalla-informativa')}
                    >
                        <i className="bi bi-tv me-2"></i>
                        Configurar Pantalla Informativa
                    </Button>
                )}
                <Button className="rec-btn-secondary" onClick={() => navigate('/recepcion/turnero')}>
                    <i className="bi bi-calendar3 me-2"></i>
                    Ir al Turnero
                </Button>
                <Button className="rec-btn-secondary" onClick={() => navigate('/recepcion')}>
                    <i className="bi bi-speedometer2 me-2"></i>
                    Ir a Panel Diario
                </Button>
            </div>

            <div className="eventos-section rec-panel atencion-section">
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-clipboard-check me-2"></i>
                        Turnos en atención
                    </h2>
                    <Badge bg="primary" className="rec-total-badge">
                        {totalTurnosActivos} en espera
                    </Badge>
                </div>

                {/* Filtro por texto — mismo estilo de campo que TurneroPage/PanelDiario. */}
                <div className={turneroStyles.filtroCampoAncho} style={{ marginBottom: '1rem' }}>
                    <label className={turneroStyles.filtroLabel}>
                        <i className="bi bi-search me-2"></i>Buscar
                    </label>
                    <input
                        type="text"
                        className={`form-control ${turneroStyles.filtroInput}`}
                        placeholder="Nombre, apellido, DNI o CUIL…"
                        value={filtroTexto}
                        onChange={(e) => setFiltroTexto(e.target.value)}
                    />
                </div>

                {/* Filtros superiores - Chips por área de atención */}
                <div className="atencion-filtros-container">
                    <div className="atencion-filtros-scroll">
                        <button
                            className={getAreaChipClass('todos')}
                            style={getAreaChipStyle('todos')}
                            onClick={() => setAreaSeleccionada('todos')}
                        >
                            <span className="area-chip-dot" style={{ backgroundColor: estiloChip('todos').color }}></span>
                            Todos
                            <span className="area-chip-contador">{turnosActivos.length}</span>
                        </button>
                        {AREAS_ATENCION.map(area => (
                            <button
                                key={area.valor}
                                className={getAreaChipClass(area.valor)}
                                style={getAreaChipStyle(area.valor)}
                                onClick={() => setAreaSeleccionada(area.valor)}
                            >
                                <span className="area-chip-dot" style={{ backgroundColor: area.color }}></span>
                                {area.etiqueta}
                                <span className="area-chip-contador">{contadoresPorArea[area.valor] || 0}</span>
                            </button>
                        ))}
                        <button
                            className={getAreaChipClass('sin_asignar')}
                            style={getAreaChipStyle('sin_asignar')}
                            onClick={() => setAreaSeleccionada('sin_asignar')}
                        >
                            <span className="area-chip-dot" style={{ backgroundColor: estiloChip('sin_asignar').color }}></span>
                            Sin asignar
                            <span className="area-chip-contador">{contadoresPorArea.sin_asignar || 0}</span>
                        </button>
                    </div>
                </div>

                {cargando ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 mb-0">Cargando turnos...</p>
                    </div>
                ) : error ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-exclamation-triangle"></i>
                        <h5>Ocurrió un problema</h5>
                        <p>{error}</p>
                    </div>
                ) : turnosActivosFiltrados.length === 0 ? (
                    <div className="empty-state-eventos">
                        <i className="bi bi-person-check"></i>
                        <h5>No hay turnos en atención</h5>
                        <p>No hay turnos con estado Asistió o En curso.</p>
                    </div>
                ) : (
                    <div className="atencion-lista-vertical">
                        {turnosActivosFiltrados.map((turno) => {
                            const idTurno = resolverIdTurno(turno);
                            const estadoInfo = getEstadoTurno(turno);
                            const esEnCurso = String(turno?.estado || '').trim().toUpperCase() === 'EN_CURSO';
                            const tipoColor = colorTramite(turno.tipoTramite);
                            const tipoBg = bgTramite(turno.tipoTramite);
                            return (
                                <AtencionCard
                                    key={idTurno ?? `${turno.apellido}-${turno.nombre}-${turno.fechaTurno}`}
                                    turno={turno}
                                    idTurno={idTurno}
                                    estadoInfo={estadoInfo}
                                    esEnCurso={esEnCurso}
                                    tipoColor={tipoColor}
                                    tipoBg={tipoBg}
                                    onCardClick={() => navegarADetalle(idTurno, turno)}
                                    onAtender={() => {
                                        if (!idTurno || esEnCurso) return;
                                        navegarADetalle(idTurno, turno);
                                    }}
                                    onDerivar={() => abrirDerivar(turno)}
                                />
                            );
                        })}
                    </div>
                )}

                {/* Turnos ANSES: carril aparte, no entran en la cola de atención.
                    Solo se muestra con "Todos" o el filtro de ANSES: al filtrar por otra área
                    no debe quedar un carril que no corresponde a lo que se está mirando. */}
                {turnosAnses.length > 0 && (areaSeleccionada === 'todos' || areaSeleccionada === 'ANSES') && (
                    <div className="atencion-anses-section">
                        <div className="eventos-header-row mt-4">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-building me-2"></i>
                                Turnos ANSES
                            </h2>
                            <Badge bg="info" className="rec-total-badge">
                                {turnosAnses.length} esperando
                            </Badge>
                        </div>
                        <p className="text-muted small mb-3">
                            No se atienden en el mostrador: esperan hasta la hora de su turno y un abogado
                            los acompaña. Entrá al turno para registrar la firma y marcar la salida.
                        </p>
                        <div className="atencion-lista-vertical">
                            {turnosAnses.map((turno) => {
                                const idTurno = resolverIdTurno(turno);
                                return (
                                    <AtencionCardAnses
                                        key={idTurno ?? `anses-${turno.apellido}-${turno.fechaTurno}`}
                                        turno={turno}
                                        estadoInfo={getEstadoTurno(turno)}
                                        onCardClick={() => navegarADetalle(idTurno, turno)}
                                        onVerDetalle={() => setDetalleAnsesId(turno.idTurnoAnses)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Sección de Turnos Finalizados */}
                {turnosFinalizados.length > 0 && (
                    <div className="atencion-finalizados-section">
                        <div
                            className="atencion-finalizados-header"
                            onClick={() => setMostrarFinalizados(!mostrarFinalizados)}
                        >
                            <div className="atencion-finalizados-titulo">
                                <i className="bi bi-check-circle-fill me-2"></i>
                                Turnos Finalizados Hoy
                                <Badge bg="success" className="ms-3">
                                    {totalFinalizados} atendidos
                                </Badge>
                            </div>
                            <div className="atencion-finalizados-toggle">
                                <i className={`bi bi-chevron-${mostrarFinalizados ? 'up' : 'down'}`}></i>
                            </div>
                        </div>

                        <Collapse in={mostrarFinalizados}>
                            <div className="atencion-finalizados-lista">
                                {turnosFinalizados.map((turno) => {
                                    const idTurno = resolverIdTurno(turno);
                                    const estadoInfo = getEstadoTurno(turno);
                                    const tipoColor = colorTramite(turno.tipoTramite);
                                    const tipoBg = bgTramite(turno.tipoTramite);
                                    return (
                                        <AtencionCardFinalizado
                                            key={idTurno ?? `${turno.apellido}-${turno.nombre}-${turno.fechaTurno}`}
                                            turno={turno}
                                            idTurno={idTurno}
                                            estadoInfo={estadoInfo}
                                            tipoColor={tipoColor}
                                            tipoBg={tipoBg}
                                            onCardClick={() => navegarADetalle(idTurno, turno)}
                                            onVolvioDeAnses={() => setTurnoVuelveDeAnses(turno)}
                                        />
                                    );
                                })}
                            </div>
                        </Collapse>
                    </div>
                )}
            </div>

            <Modal className="rec-modal" show={!!turnoDerivar} onHide={cerrarDerivar} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-signpost-2 me-2"></i>
                        Derivar turno a otra área
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {turnoDerivar && (
                        <div className="rec-modal-resumen atencion-derivar-resumen">
                            <strong>{apellidoNombre(turnoDerivar.apellido, turnoDerivar.nombre) || '-'}</strong>
                        </div>
                    )}
                    <label className="form-label fw-semibold mt-3 mb-2">Nueva área de atención *</label>
                    <SelectorAreaAtencion valor={areaDerivar} onChange={setAreaDerivar} disabled={guardandoDerivar} />
                    {errorDerivar && <div className="alert alert-danger py-2 mt-3 mb-0">{errorDerivar}</div>}
                </Modal.Body>
                <Modal.Footer>
                    <button className="rec-btn-secondary" onClick={cerrarDerivar} disabled={guardandoDerivar}>Cancelar</button>
                    <button className="rec-btn-primary" onClick={confirmarDerivar} disabled={!areaDerivar || guardandoDerivar}>
                        {guardandoDerivar ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
                        Derivar
                    </button>
                </Modal.Footer>
            </Modal>

            <ModalReingresoCliente
                show={!!turnoVuelveDeAnses}
                turnoOrigen={turnoVuelveDeAnses}
                tipoInicial={TIPO_APERTURA_PLAN}
                tipoFijo
                onCerrar={() => setTurnoVuelveDeAnses(null)}
                onRegistrado={() => { setTurnoVuelveDeAnses(null); cargarTurnos(true); }}
            />

            <ModalDetalleTurnoAnses
                idTurnoAnses={detalleAnsesId}
                onClose={() => setDetalleAnsesId(null)}
            />
        </div>
    );
};
/**
 * Tarjeta del acompañamiento a ANSES.
 *
 * Usa la MISMA estructura de tres secciones que AtencionCard (izquierda / central / derecha) para
 * que la cola se lea igual de arriba abajo: antes era un flex plano y en el listado se veía como
 * otra cosa. Lo propio de ANSES —la hora en el organismo, la UDAI, la firma pendiente— se suma
 * como badges, en vez de cambiar el layout.
 *
 * Lo que sí cambia es la sección derecha: estos turnos no se atienden en el mostrador (el cliente
 * espera y un abogado lo acompaña), así que en lugar de "Atender" va el acceso al detalle.
 */
const AtencionCardAnses = ({ turno, estadoInfo, onCardClick, onVerDetalle }) => {
    const { label, icon, className } = estadoInfo;
    const firmaPendiente = turno.requiereFirma && !turno.firmaResuelta;
    const areaAnses = buscarArea('ANSES');
    const tipoTramiteFormateado = formatearTipoTramite(turno.tipoTramite);
    const tipoColor = colorTramite(turno.tipoTramite);
    const tipoBg = bgTramite(turno.tipoTramite);

    return (
        <div
            className={`atencion-card-vertical ${className} ${firmaPendiente ? 'atencion-card-firma-pendiente' : ''}`}
            onClick={onCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick();
                }
            }}
        >
            <span className="rec-item-riel" style={{ background: estadoInfo.riel }} />

            {/* Izquierda: quién es */}
            <div className="atencion-card-seccion atencion-card-seccion-izquierda">
                <div className="atencion-card-nombre">
                    {apellidoNombre(turno.apellido, turno.nombre) || '-'}
                </div>
                <div className="atencion-card-identificacion">
                    <i className="bi bi-person-badge me-1" />
                    {campoValor(turno.cuil || turno.dni || turno.numeroIdentificacion)}
                </div>
                <span
                    className="rec-badge-estado atencion-card-estado-badge"
                    style={{ background: estadoInfo.bg, border: `1px solid ${estadoInfo.border}`, color: estadoInfo.text }}
                >
                    <i className={`bi ${icon} me-1`}></i>
                    {label}
                </span>
            </div>

            {/* Centro: a qué vino y cuándo — lo que faltaba en esta tarjeta */}
            <div className="atencion-card-seccion atencion-card-seccion-central">
                <span
                    className="rec-chip-tramite atencion-tramite-badge"
                    style={{ backgroundColor: tipoBg, color: tipoColor, borderColor: `${tipoColor}33` }}
                >
                    <i className="bi bi-receipt atencion-tramite-badge-icono me-1" style={{ color: tipoColor }} />
                    {tipoTramiteFormateado}
                </span>
                {areaAnses && (
                    <span
                        className="rec-chip-area atencion-area-badge"
                        style={{ backgroundColor: areaAnses.bgColor, color: areaAnses.color, borderColor: `${areaAnses.color}33` }}
                    >
                        <i className={`bi ${areaAnses.icono} me-1`}></i>
                        {areaAnses.etiqueta}
                    </span>
                )}
                {firmaPendiente && (
                    <span className="rec-badge-estado atencion-card-firma-badge">
                        <i className="bi bi-pencil-square me-1"></i>
                        Falta firmar{turno.documentacionAFirmar ? `: ${turno.documentacionAFirmar}` : ''}
                    </span>
                )}
                {turno.motivo && (
                    <div className="atencion-card-observacion">
                        <i className="bi bi-chat-left-text me-1"></i>
                        <span>{turno.motivo}</span>
                    </div>
                )}
                <div className="atencion-card-detalles">
                    <span className="atencion-detalle-item">
                        <i className="bi bi-calendar3 me-1" />
                        {formatearFecha(turno.fechaTurno)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-clock me-1" />
                        Citado {formatearHorario(turno.horario)}
                    </span>
                    {/* Vista y citación no llevan nada a presentar: quien lo atienda tiene que
                        saberlo antes de preparar documentación que no va. */}
                    {esSinPresentacion(turno.tipoTurnoAnses) && (
                        <span className="atencion-detalle-item atencion-detalle-anses">
                            <i className={`bi ${metaTipoTurnoAnses(turno.tipoTurnoAnses).icono} me-1`}></i>
                            {metaTipoTurnoAnses(turno.tipoTurnoAnses).detalleTurnero}
                        </span>
                    )}
                    {/* La hora que de verdad importa acá: a la que tiene que estar en el organismo. */}
                    {turno.horaTurnoAnses && (
                        <span className="atencion-detalle-item atencion-detalle-anses">
                            <i className="bi bi-building me-1"></i>
                            En ANSES {turno.horaTurnoAnses}
                        </span>
                    )}
                    {turno.udaiLugar && (
                        <span className="atencion-detalle-item">
                            <i className="bi bi-geo-alt me-1"></i>
                            {turno.udaiLugar}
                        </span>
                    )}
                    <span className="atencion-detalle-item">
                        <i className="bi bi-telephone me-1" />
                        {campoValor(turno.telefonoCliente)}
                    </span>
                </div>
            </div>

            {/* Derecha: no se atiende en el mostrador, se entra al detalle */}
            <div className="atencion-card-seccion atencion-card-seccion-derecha">
                <span className="text-muted small mb-2">{turno.numeroIdentificacion}</span>
                {turno.idTurnoAnses && (
                    <Button
                        className="rec-btn-primary atencion-btn-atender"
                        onClick={(e) => { e.stopPropagation(); onVerDetalle && onVerDetalle(); }}
                    >
                        <i className="bi bi-eye me-2"></i>
                        Ver detalle
                    </Button>
                )}
            </div>
        </div>
    );
};

// Componente AtencionCard para turnos activos (ASISTIO y EN_CURSO)
const AtencionCard = ({ turno, idTurno, estadoInfo, esEnCurso, tipoColor, tipoBg, onCardClick, onAtender, onDerivar }) => {
    const { label, icon, className } = estadoInfo;
    const tipoTramiteFormateado = formatearTipoTramite(turno.tipoTramite);
    const areaInfo = buscarArea(turno.atencion);

    return (
        <div
            className={`atencion-card-vertical ${className}`}
            onClick={onCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick();
                }
            }}
        >
            {/* Indicador visual de estado (barra lateral) */}
            <span className="rec-item-riel" style={{ background: estadoInfo.riel }} />

            {/* Derivar a otra área (recepción se equivocó de destino) */}
            <button
                type="button"
                className="atencion-card-menu-btn"
                title="Derivar a otra área"
                onClick={(e) => {
                    e.stopPropagation();
                    onDerivar();
                }}
            >
                <i className="bi bi-three-dots-vertical"></i>
            </button>

            {/* Sección Izquierda - Nombre y Apellido */}
            <div className="atencion-card-seccion atencion-card-seccion-izquierda">
                <div className="atencion-card-nombre">
                    {apellidoNombre(turno.apellido, turno.nombre) || '-'}
                </div>
                <div className="atencion-card-identificacion">
                    <i className="bi bi-person-badge me-1" />
                    {campoValor(turno.dni || turno.cuil || turno.numeroIdentificacion)}
                </div>
                <span
                    className="rec-badge-estado atencion-card-estado-badge"
                    style={{ background: estadoInfo.bg, border: `1px solid ${estadoInfo.border}`, color: estadoInfo.text }}
                >
                    <i className={`bi ${icon} me-1`}></i>
                    {label}
                </span>
            </div>

            {/* Sección Central - Tipo de Trámite como badge diferenciado */}
            <div className="atencion-card-seccion atencion-card-seccion-central">
                <span
                    className="rec-chip-tramite atencion-tramite-badge"
                    style={{ backgroundColor: tipoBg, color: tipoColor, borderColor: `${tipoColor}33` }}
                >
                    <i className="bi bi-receipt atencion-tramite-badge-icono me-1" style={{ color: tipoColor }} />
                    {tipoTramiteFormateado}
                </span>
                {areaInfo && (
                    <span
                        className="rec-chip-area atencion-area-badge"
                        style={{ backgroundColor: areaInfo.bgColor, color: areaInfo.color, borderColor: `${areaInfo.color}33` }}
                    >
                        <i className={`bi ${areaInfo.icono} me-1`}></i>
                        {areaInfo.etiqueta}
                    </span>
                )}
                {turno.motivo && (
                    <div className="atencion-card-observacion">
                        <i className="bi bi-chat-left-text me-1"></i>
                        <span>{turno.motivo}</span>
                    </div>
                )}
                <div className="atencion-card-detalles">
                    <span className="atencion-detalle-item">
                        <i className="bi bi-calendar3 me-1" />
                        {formatearFecha(turno.fechaTurno)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-clock me-1" />
                        {formatearHorario(turno.horario)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-telephone me-1" />
                        {campoValor(turno.telefonoCliente)}
                    </span>
                </div>
                {turno.esSobreturno && !turno.esReingreso && (
                    <div className="rec-badge-sobreturno atencion-card-sobreturno">
                        <i className="bi bi-exclamation-diamond me-1"></i>
                        Sobreturno
                    </div>
                )}
                {/* Ya estuvo hoy en el estudio: quien lo atienda sabe que viene de una atención previa. */}
                <BadgeReingreso turno={turno} className="atencion-card-sobreturno" />
                {esEnCurso && (
                    <div className="atencion-card-operador">
                        <i className="bi bi-person-badge me-1"></i>
                        Atendiendo: {nombreOperador(turno)}
                    </div>
                )}
            </div>

            {/* Sección Derecha - Botón Atender con color unificado */}
            <div className="atencion-card-seccion atencion-card-seccion-derecha">
                {esEnCurso ? (
                    <div className="atencion-estado-en-curso-boton" onClick={(e) => e.stopPropagation()}>
                        <div className="atencion-pulse-container">
                            <span className="atencion-pulse-dot"></span>
                            <span className="atencion-pulse-text">
                                <i className="bi bi-play-circle-fill me-2"></i>
                                En curso
                            </span>
                            <span className="atencion-pulse-ring"></span>
                        </div>
                    </div>
                ) : (
                    <Button
                        className="rec-btn-primary atencion-btn-atender"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAtender();
                        }}
                    >
                        <i className="bi bi-person-arms-up me-2"></i>
                        Atender
                    </Button>
                )}
            </div>
        </div>
    );
};

// Componente AtencionCard para turnos finalizados (más compacto)
const AtencionCardFinalizado = ({ turno, idTurno, estadoInfo, tipoColor, tipoBg, onCardClick, onVolvioDeAnses }) => {
    const { label, icon, className } = estadoInfo;
    const tipoTramiteFormateado = formatearTipoTramite(turno.tipoTramite);

    return (
        <div
            className={`atencion-card-finalizado ${className}`}
            onClick={onCardClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick();
                }
            }}
        >
            <div className="atencion-card-finalizado-indicador"></div>

            <div className="atencion-card-finalizado-contenido">
                <div className="atencion-card-finalizado-info">
                    <span className="atencion-card-finalizado-nombre">
                        {apellidoNombre(turno.apellido, turno.nombre) || '-'}
                    </span>
                    <span
                        className="atencion-card-finalizado-tramite"
                        style={{ backgroundColor: tipoBg, color: tipoColor, borderColor: `${tipoColor}33` }}
                    >
                        <i className="bi bi-receipt atencion-tramite-badge-icono me-1" style={{ color: tipoColor }} />
                        {tipoTramiteFormateado}
                    </span>
                    <BadgeReingreso turno={turno} className="atencion-card-sobreturno" />
                </div>

                <div className="atencion-card-finalizado-detalles">
                    <span className="atencion-detalle-item">
                        <i className="bi bi-person-badge me-1" />
                        {campoValor(turno.dni || turno.cuil || turno.numeroIdentificacion)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-calendar3 me-1" />
                        {formatearFecha(turno.fechaTurno)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-clock me-1" />
                        {formatearHorario(turno.horario)}
                    </span>
                    <span className="atencion-detalle-item">
                        <i className="bi bi-person-badge me-1"></i>
                        Atendido por: {nombreOperador(turno)}
                    </span>
                </div>
            </div>

            {/* Salió hacia ANSES por el plan de pago y todavía no volvió: desde acá se marca el retorno. */}
            {turno.vuelvePorPlan && onVolvioDeAnses && (
                <button
                    type="button"
                    className="rec-btn-primary btn-sm me-2"
                    title="Volvió de ANSES para abrir el plan de pago"
                    onClick={(e) => { e.stopPropagation(); onVolvioDeAnses(); }}
                >
                    <i className="bi bi-arrow-return-left me-1"></i>
                    Volvió de ANSES
                </button>
            )}
            <span
                className="rec-badge-estado atencion-card-finalizado-badge"
                style={{ background: estadoInfo.bg, border: `1px solid ${estadoInfo.border}`, color: estadoInfo.text }}
            >
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
        </div>
    );
};




export default AtencionPage;